import React, { useState, useEffect } from 'react';
import { Box, AppBar, Toolbar, Typography, IconButton, Avatar, Tooltip, Badge } from '@mui/material';
import { Menu as MenuIcon, Notifications, Person, Wifi, WifiOff } from '@mui/icons-material';
import { motion } from 'framer-motion';
import RealTimeClock from './RealTimeClock';
import WorkingTimeTracker from './WorkingTimeTracker';
import { useAuth } from '../context/AuthContext';
import { settingsAPI } from '../services/api';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5076/api';

const Header = ({ onMenuClick, onNotificationsClick, onProfileClick, unreadCount = 0 }) => {
  const { user } = useAuth();
  const [logoUrl, setLogoUrl] = useState('');
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  useEffect(() => {
    loadLogo();

    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const loadLogo = async () => {
    try {
      const response = await settingsAPI.getByKey('logo_url');
      if (response.data.success && response.data.data) {
        setLogoUrl(response.data.data.value || '');
      }
    } catch (error) {
      console.log('Logo not set yet');
    }
  };

  const getProfilePhoto = () => {
    if (user?.profile_image) {
      return user.profile_image.startsWith('http') 
        ? user.profile_image 
        : `${API_URL.replace('/api', '')}${user.profile_image}`;
    }
    if (user?.profile_photo) {
      return user.profile_photo.startsWith('http') 
        ? user.profile_photo 
        : `${API_URL.replace('/api', '')}${user.profile_photo}`;
    }
    return null;
  };

  const profilePhoto = getProfilePhoto();

  return (
    <AppBar 
      position="fixed" 
      sx={{ 
        zIndex: (theme) => theme.zIndex.drawer + 1,
        background: 'linear-gradient(135deg, rgba(15, 39, 68, 0.95) 0%, rgba(10, 25, 41, 0.98) 100%)',
        backdropFilter: 'blur(20px)',
        boxShadow: '0 4px 30px rgba(0,0,0,0.3), 0 1px 0 rgba(212,175,55,0.2)',
        borderBottom: '1px solid rgba(212, 175, 55, 0.15)',
      }}
    >
      <Toolbar sx={{ justifyContent: 'space-between', minHeight: '64px !important', px: { xs: 1, sm: 2 } }}>
        {/* Left side */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <motion.div
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
          >

          </motion.div>
          
          {logoUrl ? (
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
            >
                <img
                  src={logoUrl.startsWith('http') ? logoUrl : `${API_URL.replace('/api', '')}${logoUrl}`}
                  alt="Company Logo"
                  className="header-logo"
                />
            </motion.div>
          ) : (
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
            >
              <Typography
                variant="h6"
                sx={{
                  background: 'linear-gradient(135deg, #D4AF37 0%, #F4D03F 100%)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  fontWeight: 800,
                  letterSpacing: '1px',
                  fontSize: { xs: '0.95rem', sm: '1.25rem' },
                  textShadow: '0 0 20px rgba(212,175,55,0.3)',
                }}
              >
                Smart Attendance
              </Typography>
            </motion.div>
          )}
        </Box>

        {/* Center - Working Time Tracker */}
        <Box sx={{ display: { xs: 'none', md: 'flex' }, flexGrow: 1, justifyContent: 'center' }}>
          <WorkingTimeTracker />
        </Box>

        {/* Right side */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          {/* Connection Status */}
          <Tooltip title={isOnline ? 'Connected' : 'Offline'}>
            <motion.div
              animate={{ opacity: isOnline ? 1 : 0.5 }}
              transition={{ duration: 0.3 }}
            >
              <Box
                sx={{
                  display: { xs: 'none', sm: 'flex' },
                  alignItems: 'center',
                  gap: 0.5,
                  px: 1.5,
                  py: 0.5,
                  borderRadius: 2,
                  bgcolor: isOnline ? 'rgba(76, 175, 80, 0.1)' : 'rgba(239, 83, 80, 0.1)',
                  border: `1px solid ${isOnline ? 'rgba(76, 175, 80, 0.3)' : 'rgba(239, 83, 80, 0.3)'}`,
                }}
              >
                {isOnline ? (
                  <Wifi sx={{ fontSize: 16, color: '#66BB6A' }} />
                ) : (
                  <WifiOff sx={{ fontSize: 16, color: '#EF5350' }} />
                )}
                <Typography
                  variant="caption"
                  sx={{
                    color: isOnline ? '#66BB6A' : '#EF5350',
                    fontWeight: 600,
                    fontSize: '0.65rem',
                  }}
                >
                  {isOnline ? 'LIVE' : 'OFF'}
                </Typography>
              </Box>
            </motion.div>
          </Tooltip>

          {/* Real Time Clock */}
          <RealTimeClock />

          {/* Notifications */}
          <motion.div
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <IconButton 
              onClick={onNotificationsClick}
              sx={{ 
                color: '#D4AF37',
                transition: 'all 0.3s ease',
                '&:hover': { 
                  bgcolor: 'rgba(212,175,55,0.15)',
                  boxShadow: '0 0 20px rgba(212,175,55,0.2)',
                },
              }}
            >
              <Badge 
                badgeContent={unreadCount} 
                sx={{
                  '& .MuiBadge-badge': {
                    background: 'linear-gradient(135deg, #EF5350, #C62828)',
                    color: '#FFF',
                    fontWeight: 700,
                    fontSize: '0.65rem',
                    minWidth: 20,
                    height: 20,
                    borderRadius: '10px',
                    border: '2px solid #0A1929',
                  }
                }}
              >
                <Notifications />
              </Badge>
            </IconButton>
          </motion.div>

          {/* Profile */}
          <Tooltip title={user ? `${user.first_name} ${user.last_name}` : 'Profile'}>
            <motion.div
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <IconButton 
                onClick={onProfileClick}
                sx={{ 
                  p: 0.5,
                  border: '2px solid rgba(212, 175, 55, 0.4)',
                  borderRadius: '50%',
                  transition: 'all 0.3s ease',
                  '&:hover': { 
                    border: '2px solid #D4AF37',
                    bgcolor: 'rgba(212,175,55,0.1)',
                    boxShadow: '0 0 20px rgba(212,175,55,0.3)',
                  },
                }}
              >
                {profilePhoto ? (
                  <Avatar 
                    src={profilePhoto} 
                    alt={user?.first_name}
                    sx={{ 
                      width: 34, 
                      height: 34,
                      bgcolor: 'rgba(212,175,55,0.2)',
                      border: '1px solid rgba(212,175,55,0.3)',
                    }}
                  />
                ) : (
                  <Avatar 
                    sx={{ 
                      width: 34, 
                      height: 34,
                      bgcolor: 'rgba(212,175,55,0.2)',
                      color: '#D4AF37',
                      fontWeight: 'bold',
                      fontSize: '0.9rem',
                    }}
                  >
                    {user?.first_name?.[0]}{user?.last_name?.[0]}
                  </Avatar>
                )}
              </IconButton>
            </motion.div>
          </Tooltip>
        </Box>
      </Toolbar>
    </AppBar>
  );
};

export default Header;